import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import Layout from "../components/Layout";
import {
  FaHome,
  FaBook,
  FaMicroscope,
  FaFutbol,
  FaMusic,
  FaLaptop,
  FaSchool,
  FaFlask,
  FaPaintBrush,
  FaBus,
  FaShieldAlt,
  FaTree,
  FaUsers,
  FaLightbulb,
  FaBookOpen,
  FaChessBoard,
  FaVideo,
  FaCamera,
  FaMapMarkerAlt,
  FaStar,
  FaGraduationCap,
  FaHeart,
  FaSearch
} from "react-icons/fa";
import BreadCrumbImage from "../assets/images/breadcrumb.png";

const Facilities = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const facilities = [
    {
      id: 1,
      title: "Digital Library",
      description: "10,000+ books with digital catalog and reading area",
      icon: FaBook,
      category: "academic",
      color: "from-blue-500 to-cyan-500",
      features: ["10K+ Books", "Digital Catalog", "Reading Zone", "E-Books"]
    },
    {
      id: 2,
      title: "Science Laboratories",
      description: "Modern labs for Physics, Chemistry & Biology",
      icon: FaMicroscope,
      category: "academic",
      color: "from-emerald-500 to-teal-500",
      features: ["Physics Lab", "Chemistry Lab", "Biology Lab", "Equipment"]
    },
    {
      id: 3,
      title: "Computer Lab",
      description: "40+ computers with latest software and internet",
      icon: FaLaptop,
      category: "academic",
      color: "from-purple-500 to-pink-500",
      features: ["40+ Systems", "Latest Software", "Internet", "Coding"]
    },
    {
      id: 4,
      title: "Sports Complex",
      description: "Indoor and outdoor sports facilities",
      icon: FaFutbol,
      category: "sports",
      color: "from-red-500 to-orange-500",
      features: ["Basketball", "Football", "Cricket", "Badminton"]
    },
    {
      id: 5,
      title: "Music & Arts Room",
      description: "Creative space for music and fine arts",
      icon: FaMusic,
      category: "arts",
      color: "from-pink-500 to-rose-500",
      features: ["Instruments", "Art Supplies", "Dance Room", "Theatre"]
    },
    {
      id: 6,
      title: "Smart Classrooms",
      description: "Digital boards and multimedia learning",
      icon: FaSchool,
      category: "academic",
      color: "from-indigo-500 to-blue-500",
      features: ["Smart Boards", "Projectors", "Audio Systems", "WiFi"]
    },
    {
      id: 7,
      title: "Activity Rooms",
      description: "Special rooms for co-curricular activities",
      icon: FaPaintBrush,
      category: "arts",
      color: "from-amber-500 to-yellow-500",
      features: ["Arts & Crafts", "Drama", "Debate", "Club Activities"]
    },
    {
      id: 8,
      title: "Science Park",
      description: "Outdoor learning area with scientific models",
      icon: FaFlask,
      category: "academic",
      color: "from-green-500 to-emerald-500",
      features: ["Models", "Experiments", "Learning Zone", "Projects"]
    },
    {
      id: 9,
      title: "Transport Facility",
      description: "GPS enabled buses with trained drivers",
      icon: FaBus,
      category: "infrastructure",
      color: "from-gray-600 to-gray-800",
      features: ["GPS Buses", "Safety", "Routes", "Attendants"]
    },
    {
      id: 10,
      title: "Security Systems",
      description: "24/7 CCTV surveillance and security",
      icon: FaShieldAlt,
      category: "infrastructure",
      color: "from-blue-600 to-indigo-600",
      features: ["CCTV", "Security Staff", "Entry Control", "Emergency"]
    },
    {
      id: 11,
      title: "Green Campus",
      description: "Eco-friendly environment with gardens",
      icon: FaTree,
      category: "infrastructure",
      color: "from-green-600 to-teal-600",
      features: ["Gardens", "Play Area", "Environment", "Rainwater"]
    },
    {
      id: 12,
      title: "Assembly Hall",
      description: "Large hall for gatherings and events",
      icon: FaUsers,
      category: "infrastructure",
      color: "from-purple-600 to-pink-600",
      features: ["500 Capacity", "Stage", "Sound System", "Events"]
    }
  ];

  const categories = [
    { id: "all", label: "All Facilities", icon: FaSchool, count: facilities.length },
    { id: "academic", label: "Academic", icon: FaBook, count: facilities.filter(f => f.category === "academic").length },
    { id: "sports", label: "Sports", icon: FaFutbol, count: facilities.filter(f => f.category === "sports").length },
    { id: "arts", label: "Arts", icon: FaMusic, count: facilities.filter(f => f.category === "arts").length },
    { id: "infrastructure", label: "Infrastructure", icon: FaShieldAlt, count: facilities.filter(f => f.category === "infrastructure").length }
  ];

  const highlightFeatures = [
    { icon: FaVideo, title: "Smart Classes", description: "Digital learning environment" },
    { icon: FaCamera, title: "CCTV Security", description: "24/7 campus surveillance" },
    { icon: FaMapMarkerAlt, title: "Transport", description: "Safe bus service" },
    { icon: FaStar, title: "Green Campus", description: "Eco-friendly environment" }
  ];

  const stats = [
    { value: "12+", label: "Modern Facilities", icon: FaSchool },
    { value: "40+", label: "Computer Systems", icon: FaLaptop },
    { value: "10K+", label: "Library Books", icon: FaBook },
    { value: "100%", label: "Safe Campus", icon: FaShieldAlt }
  ];

  const filteredFacilities = facilities.filter(facility => 
    (selectedCategory === "all" || facility.category === selectedCategory) &&
    (searchTerm === "" || 
      facility.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      facility.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Our Facilities | School</title>
        <meta name="description" content="Explore our modern school facilities - Digital library, science labs, sports complex, smart classrooms, and more." />
      </Helmet>

      {/* Breadcrumb */}
      <div className="relative bg-gray-900">
        <div className="h-40 md:h-48">
          <img src={BreadCrumbImage} alt="Breadcrumb" className="object-cover w-full h-full opacity-60" />
          <div className="absolute inset-0 bg-black/50" />
        </div>

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className="flex items-center mb-2 text-xs text-white">
            <Link to="/" className="flex items-center transition-colors hover:text-gray-200">
              <FaHome className="w-3 h-3 mr-1" />
              Home
            </Link>
            <span className="mx-1">/</span>
            <span className="font-semibold">Facilities</span>
          </div>
          <h1 className="text-2xl font-bold text-white md:text-3xl">Our Facilities</h1>
          <p className="mt-1 text-xs text-gray-300">Modern infrastructure for holistic education</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container px-3 py-6 mx-auto md:px-4 md:py-8">
        
        {/* Stats */}
        <motion.div 
          className="grid grid-cols-2 gap-3 mb-6 md:grid-cols-4"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="p-4 text-center transition-shadow bg-white border border-gray-200 rounded-xl hover:shadow-md"
            >
              <div className="inline-flex items-center justify-center w-10 h-10 mb-2 rounded-full bg-blue-50">
                <stat.icon className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-lg font-bold text-gray-900 md:text-xl">{stat.value}</div>
              <div className="text-xs text-gray-600">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Search & Filter */}
        <div className="p-4 mb-6 bg-white border border-gray-200 rounded-xl">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            {/* Search */}
            <div className="relative flex-1">
              <FaSearch className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
              <input
                type="text"
                placeholder="Search facilities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-2 pl-10 pr-4 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    selectedCategory === category.id 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <category.icon className="w-4 h-4" />
                  {category.label}
                  <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                    selectedCategory === category.id 
                      ? 'bg-white/20' 
                      : 'bg-gray-200'
                  }`}>
                    {category.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Highlight Features */}
        <div className="mb-8">
          <h3 className="mb-4 text-lg font-bold text-center text-gray-900">Key Features</h3>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {highlightFeatures.map((feature, index) => (
              <div key={index} className="p-4 text-center transition-shadow bg-white border border-gray-200 rounded-xl hover:shadow-sm">
                <div className="inline-flex items-center justify-center w-12 h-12 mb-3 rounded-full bg-blue-50">
                  <feature.icon className="w-6 h-6 text-blue-600" />
                </div>
                <div className="text-sm font-bold text-gray-900">{feature.title}</div>
                <div className="mt-1 text-xs text-gray-600">{feature.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Facilities Grid */}
        {filteredFacilities.length === 0 ? (
          <div className="p-8 text-center bg-white border border-gray-200 rounded-xl">
            <FaSchool className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-600">No facilities found for the selected filter.</p>
            <button 
              onClick={() => {setSelectedCategory("all"); setSearchTerm("");}}
              className="px-4 py-2 mt-3 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <motion.div 
            className="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {filteredFacilities.map(facility => (
              <motion.div
                key={facility.id}
                variants={itemVariants}
                whileHover={{ y: -4 }}
                className="overflow-hidden transition-all bg-white border border-gray-200 shadow-sm rounded-xl hover:shadow-md"
              >
                {/* Header */}
                <div className={`p-5 bg-gradient-to-r ${facility.color} text-white`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-2 rounded-lg bg-white/20 backdrop-blur-sm">
                      <facility.icon className="w-6 h-6" />
                    </div>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-white/20 backdrop-blur-sm">
                      {facility.category === 'academic' ? 'Academic' :
                       facility.category === 'sports' ? 'Sports' :
                       facility.category === 'arts' ? 'Arts' : 'Infrastructure'}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold">{facility.title}</h3>
                </div>

                {/* Content */}
                <div className="p-5">
                  <p className="mb-4 text-sm text-gray-700">{facility.description}</p>
                  
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {facility.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                        <span className="text-xs text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="pt-4 border-t border-gray-100">
                    <button className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700">
                      Learn More
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Additional Information */}
        <div className="grid gap-6 mt-8 md:grid-cols-2">
          {/* Safety & Security */}
          <div className="p-5 border border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FaShieldAlt className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Safety & Security</h3>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <FaHeart className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>24/7 CCTV surveillance across campus</span>
              </li>
              <li className="flex items-start gap-2">
                <FaHeart className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>Trained security staff and entry control</span>
              </li>
              <li className="flex items-start gap-2">
                <FaHeart className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>Medical room with first aid facilities</span>
              </li>
              <li className="flex items-start gap-2">
                <FaHeart className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>Fire safety equipment and emergency plans</span>
              </li>
            </ul>
          </div>

          {/* Technology Integration */}
          <div className="p-5 border border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FaLightbulb className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Technology Integration</h3>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <FaGraduationCap className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                <span>High-speed WiFi across campus</span>
              </li>
              <li className="flex items-start gap-2">
                <FaGraduationCap className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                <span>Digital attendance and communication system</span>
              </li>
              <li className="flex items-start gap-2">
                <FaGraduationCap className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                <span>Online learning resources and platforms</span>
              </li>
              <li className="flex items-start gap-2">
                <FaGraduationCap className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                <span>Digital library with e-books and journals</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Facilities;